<template>
  <div class="theme-details-form default-content">
    <div class="container-shadow">
      <!-- Household level: one field for the whole household, above the person selector. -->
      <div class="theme-details-form__household details-section">
        <div class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
          <theme-select
            :label="subscriptionNumberOfAdults"
            v-model.number="number_of_adults_value"
            :name="'number_of_adults'"
            :options="{
              '1': '1',
              '2': '2',
              '3': '3',
              '4': '4',
              '5': '5',
            }"
            v-validate="`required`"
          ></theme-select>
        </div>
      </div>

      <!--
        Person selector with the look of the upload-documents candidate selector (.document-tabs).
        Every panel stays mounted (v-show), so the fields of the hidden persons are still validated
        and submitted with the one form.
      -->
      <div class="document-tabs theme-details-form__persons">
        <ul class="document-tabs__list document-tabs__list--candidates" role="tablist">
          <li
            v-for="person in persons"
            :key="person.number"
            class="document-tabs__item"
            role="presentation"
          >
            <button
              type="button"
              role="tab"
              class="document-tabs__link document-tabs__link--button"
              :class="{ active: person.number === active_person }"
              :id="personTabId(person.number)"
              :aria-selected="person.number === active_person ? 'true' : 'false'"
              :aria-controls="personPanelId(person.number)"
              :tabindex="person.number === active_person ? 0 : -1"
              @click="selectPerson(person.number)"
              @keydown="onPersonTabKeydown($event, person.number)"
            >{{ person.label }}</button>
          </li>
        </ul>
      </div>

      <div
        v-show="active_person === 1"
        class="theme-details-form__person-panel"
        role="tabpanel"
        :id="personPanelId(1)"
        :aria-labelledby="personTabId(1)"
        data-person-panel="1"
      >
      <div class="theme-details-form__inputs-list">

        <div class="theme-details-form__account-information details-section">
          <h2 class="section-title-sm">{{ accountInformationText }}</h2>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-8">
            <theme-radios
              name="languageInput"
              :options="languages"
              :value="defaultLanguage"
              :label="locales && locales.users && locales.users.language"
              v-validate="`required`"
              :isRequired="true"
            ></theme-radios>

            <theme-radios
              name="business_registration"
              :options="businessRadioOptions"
              :value="business_form_state"
              :label="businessLabel"
              v-validate="`required|make_choice`"
              :isRequired="true"
              @input="triggerBusinessForm"
            ></theme-radios>
          </div>

          <!-- if business registration -->
          <div v-if="business_form_state === '1'" class="grid grid-cols-1 gap-5 my-8 md:grid-cols-2 md:gap-8">
            <input
              type="hidden"
              name="business_registration"
              value="1"
              v-if="website == 'staffhousing'"
            />

            <theme-input
              :label="companyName"
              :name="companyNameInput"
              :placeholder="companyNamePlaceholder"
              v-model="company_value"
              v-validate="`required`"
              :isRequired="true"
            ></theme-input>

            <!-- if staffhousing -->
            <div v-if="website == 'staffhousing'">
              <theme-input
                :label="(locales && locales.subscriptions && locales.subscriptions.contact_person + ' ') + subscriptionsFirstName"
                :name="subscriptionsFirstNameInput"
                :placeholder="subscriptionsFirstNamePlaceholder"
                v-model="subscriptions_first_name_value"
                v-validate="`alpha_spaces_dash`"
              ></theme-input>

              <theme-input
                :label="(locales && locales.subscriptions && locales.subscriptions.contact_person + ' ') + subscriptionsMiddleName"
                :name="subscriptionsMiddleNameInput"
                :placeholder="subscriptionsMiddleNamePlaceholder"
                v-model="subscriptions_middle_name_value"
              ></theme-input>

              <theme-input
                :label="(locales && locales.subscriptions && locales.subscriptions.contact_person + ' ') + subscriptionsLastName"
                :name="subscriptionsLastNameInput"
                :placeholder="subscriptionsLastNamePlaceholder"
                v-model="subscriptions_last_name_value"
                v-validate="`alpha_spaces_dash`"
              ></theme-input>
            </div>
            <!-- else (only legal form) -->
            <theme-input
              :label="companyLegalForm"
              :name="companyLegalFormInput"
              :placeholder="companyLegalFormPlaceholder"
              v-model="company_legal_form_value"
            ></theme-input>

            <theme-input
              :label="companyChamberOfCommerceNumber"
              :name="companyChamberOfCommerceNumberInput"
              :placeholder="companyChamberOfCommerceNumberPlaceholder"
              v-model=" company_chamber_of_commerce_number_value"
              v-validate="`alpha_num_spaces`"
            ></theme-input>

            <theme-input
              :label="companyVatNumber"
              :name="companyVatNumberInput"
              :placeholder="companyVatNumberPlaceholder"
              v-model="companyVatNumberValue"
              v-validate="`alpha_num_spaces`"
            ></theme-input>

            <theme-select
              :name="companyCountryCodeName"
              :label="locales && locales.users && locales.users.country_code"
              :value="company_country_code_value"
              :options="countryCodeOptions"
              @input="onCompanyCountryChanged"
            ></theme-select>

            <!-- See the identical personal-address block above for the full rationale. -->
            <google-address-typeahead
              v-if="isCompanyAddressGoogleSupported"
              :country-code="company_country_code_value"
              :suggest-url="internationalAddressSuggestUrl"
              :validate-url="internationalAddressValidateUrl"
              :locale="locale"
              theme="public"
              @resolved="onCompanyAddressResolved"
            ></google-address-typeahead>

            <theme-input
              :label="companyPostCode"
              :placeholder="companyPostCodePlaceholder"
              :name="companyPostCodeInput"
              @blur="checkPcHnCompany($event)"
              v-model="company_post_code_value"
            ></theme-input>

            <theme-input
              :label="companyHouseNumber"
              :placeholder="companyHouseNumberPlaceholder"
              :name="companyHouseNumberInput"
              @blur="checkPostcodeAddressCompany($event)"
              v-model="company_house_number_value"
            ></theme-input>

            <theme-input
              :label="companyStreet"
              :name="companyStreetInput"
              :placeholder="companyStreetPlaceholder"
              v-model="company_street_value"
            ></theme-input>

            <theme-input
              :label="companyCity"
              :name="companyCityInput"
              :placeholder="companyCityPlaceholder"
              v-model="company_city_value"
            ></theme-input>

            <theme-input
              :label="companyCountry"
              :name="companyCountryInput"
              :placeholder="companyCountryPlaceholder"
              v-model="company_country_value"
            ></theme-input>

            <input type="hidden" :name="companyAdministrativeAreaName" v-model="company_administrative_area_value">
            <input type="hidden" :name="companyAddressLine2Name" v-model="company_address_line_2_value">
            <input type="hidden" :name="companyAddressSourceName" v-model="company_address_source_value">
            <input type="hidden" :name="companyValidationStatusName" v-model="company_validation_status_value">
            <input type="hidden" :name="companyProviderReferenceName" v-model="company_provider_reference_value">
            <input type="hidden" :name="companyResolutionIdName" v-model="company_resolution_id_value">

            <theme-input
              v-if="website == 'staffhousing'"
              :label="companyPhoneNumber"
              :placeholder="companyPhoneNumberPlaceholder"
              :name="companyPhoneNumberInput"
              v-validate="`required|phone_number`"
              v-model="company_phone_number_value"
            ></theme-input>
            <theme-input
              v-else
              :label="companyPhoneNumber"
              :placeholder="companyPhoneNumberPlaceholder"
              :name="companyPhoneNumberInput"
              v-model="company_phone_number_value"
            ></theme-input>

            <theme-input
              :label="companyEmail"
              :placeholder="companyEmailPlaceholder"
              v-if="website == 'staffhousing'"
              :name="companyEmailInput"
              v-validate="`required|email`"
              v-model="company_email_value"
            ></theme-input>
            <theme-input
              :label="companyEmail"
              :placeholder="companyEmailPlaceholder"
              v-else
              :name="companyEmailInput"
              v-model="company_email_value"
            ></theme-input>

            <theme-input
              :label="companyBankAccount"
              :placeholder="companyBankAccountPlaceholder"
              :name="companyBankAccountInput"
              v-model="company_bank_account_value"
            ></theme-input>

            <theme-input
              :label="companyBic"
              :placeholder="companyBicPlaceholder"
              :name="companyBicInput"
              v-model="company_bic_value"
            ></theme-input>

            <theme-input
              :label="companyInvoiceEmail"
              :placeholder="companyInvoiceEmailPlaceholder"
              :name="companyInvoiceEmailInput"
              v-model="companyInvoiceEmailValue"
            ></theme-input>

          </div>
        </div>

        <div class="theme-details-form__details details-section">
          <h2 class="section-title-xs">{{ subscriptionsYourDetails }}</h2>

          <theme-radios
            :name="subscriptionsSexInput"
            :options="subscriptionsSexOptions"
            :value="subscriptionsSexValue"
            v-validate="`required|make_choice`"
            @input="primary_title_id = $event"
          ></theme-radios>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
            <theme-input
              :label="subscriptionsFirstName"
              :name="subscriptionsFirstNameInput"
              v-model="subscriptions_first_name_value"
              v-validate="`required|alpha_spaces_dash`"
              :isRequired="websiteId != 2 ? true : false"
              :placeholder="subscriptionsFirstNamePlaceholder"
            ></theme-input>

            <theme-input
              :label="subscriptionsMiddleName"
              :name="subscriptionsMiddleNameInput"
              v-model="subscriptions_middle_name_value"
              :placeholder="subscriptionsMiddleNamePlaceholder"
            ></theme-input>

            <theme-input
              :label="subscriptionsLastName"
              :name="subscriptionsLastNameInput"
              v-model="subscriptions_last_name_value"
              v-validate="`required|alpha_spaces_dash`"
              :placeholder="subscriptionsLastNamePlaceholder"
              :isRequired="websiteId != 2 ? true : false"
            ></theme-input>

            <div>
              <theme-input
                :label="subscriptionsPhoneNumber"
                :name="subscriptionsPhoneNumberInput"
                v-model="subscriptions_phone_number_value"
                class="js-unique-phone"
                v-validate="`required|phone_number`"
                :isRequired="true"
                @blur="checkPhone"
              ></theme-input>

              <div v-if="!!is_phone_number_unique">
                <label></label>
                <p class="text-red-600 font-semibold">{{ phoneNumberAlreadyExists }}</p>
                <input
                  type="hidden"
                  name="phone_number_unique"
                  v-validate="`required`"
                />
              </div>
            </div>

            <div>
              <theme-input
                :label="subscriptionsEmail"
                :name="subscriptionsEmailInput"
                v-model="subscriptions_email_value"
                :placeholder="subscriptionsEmailPlaceholder"
                class="js-unique-mail"
                type="email"
                v-validate="`required|email`"
                :isRequired="true"
                @blur="checkMail"
              ></theme-input>

              <div v-if="is_user_already_exists == 1">
                <label></label>
                <p class="text-ted-600 font-semibold">{{ isUserAlreadyExists }}</p>
                <input
                  type="hidden"
                  name="user_already_exists"
                  v-validate="`required`"
                />
              </div>
            </div>

            <functional-calendar
                :configs='calendarConfigs'
                v-model='birthDate'
                class='single-calendar min-w-[250px] md:w-full'
                @choseDay='changeBirthDate'
            >
              <template v-slot:datePickerInput='props'>
                <theme-input
                  :label="subscriptionsDateOfBirth"
                  :value="birthDateComputed"
                  :name="subscriptionsDateOfBirthInput"
                  :isExtraInfo='true'
                  :isReadonly='true'
                  :placeholder="subscriptionsDateOfBirthPlaceholder"
                  v-validate="`required`"
                >
                  <template slot='extra-info'>
                    <i class='icon-calendar'/>
                  </template>
                </theme-input>
              </template>
            </functional-calendar>

            <theme-input
              :label="subscriptionsInvoiceEmail"
              :name="subscriptionsInvoiceEmailInput"
              v-model="subscriptions_invoice_email_value"
              :placeholder="subscriptionsInvoiceEmailPlaceholder"
              type="email"
              v-validate="`email`"
            ></theme-input>

            <theme-input
              :label="subscriptionsBankAccount"
              :placeholder="subscriptionsBankAccountPlaceholder"
              :name="subscriptionsBankAccountInput"
              v-model="subscriptions_bank_account_value"
            ></theme-input>

            <theme-input
              :label="subscriptionsBic"
              :placeholder="subscriptionsBicPlaceholder"
              :name="subscriptionsBicInput"
              v-model="subscriptions_bic_value"
            ></theme-input>
          </div>
        </div>

        <div class="theme-details-form__income details-section">
          <h2 class="section-title-xs">{{ subscriptionsIncome }}</h2>

          <theme-checkboxes
            :label="subscriptionsIAm"
            :name="subscriptionsTypeOfIncomeInput"
            :options="subscriptionsTypeOfIncomeOptions"
            :values="subscriptionsTypeOfIncomeValue"
            v-validate="`required|make_choice`"
            @input="triggerIncomeAdditionalForms"
          ></theme-checkboxes>

          <!-- working or business -->
          <div v-if="income_form_work_business">
            <theme-radios
              v-if="income_form_work"
              :label="usersContractTypeLabel"
              :name="usersContractTypeInput"
              :options="usersContractTypeOptions"
              :value="usersContractTypeValue"
              v-validate="`required|make_choice`"
              :isRequired="true"
              :columnDirection="true"
              class="mb-8"
              @input="triggerContractDuration"
            ></theme-radios>

            <div class="grid grid-cols-1 gap-5 mb-6 md:grid-cols-2 md:gap-8 md:mb-8" v-if="showDuration && income_form_work">
              <theme-input
                :label="usersContractDuration"
                :name="usersContractDurationInput"
                :placeholder="usersContractDurationPlaceholder"
                :value="usersContractDurationValue"
                v-validate="`required|numeric`"
                :isRequired="true"
              ></theme-input>

              <functional-calendar
                :configs='calendarConfigs'
                v-model='contractEndDate'
                class='single-calendar min-w-[250px] md:w-full'
                @choseDay='changeContractEndDate'
              >
                <template v-slot:datePickerInput='props'>
                  <theme-input
                    :label="usersContractEndDate"
                    :value="contractEndDateComputed"
                    :name="usersContractEndDateInput"
                    :isExtraInfo='true'
                    :isReadonly='true'
                    :placeholder="usersContractEndDatePlaceholder"
                    v-validate="`required`"
                    :isRequired="true"
                  >
                    <template slot='extra-info'>
                      <i class='icon-calendar'/>
                    </template>
                  </theme-input>
                  <input 
                    type="hidden"
                    :name="usersContractEndDateInput" 
                    :value="hiddenContractEndDate"
                  />
                </template>
              </functional-calendar>
            </div>

            <div class="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-8">
              <theme-input
                :label="usersMonthlyIncome"
                :name="usersMonthlyIncomeInput"
                v-model="users_monthly_income_value"
                v-validate="`required|numeric`"
                :placeholder="usersMonthlyIncomePlaceholder"
                :isRequired="true"
                :isExtraInfo="true"
                :isExtraNote="true"
              >
                <template slot='extra-info'>
                  €
                </template>
                <template slot='extra-note'>
                  {{ usersMonthlyIncomeNote }}
                </template>
              </theme-input>

              <theme-input
                :label="usersOccupation"
                :name="usersOccupationInput"
                v-model="users_occupation_value"
                v-validate="`required`"
                :placeholder="usersOccupationPlaceholder"
                :isRequired="true"
              ></theme-input>

              <theme-radios
                class="md:mb-8"
                :label="usersDoYouHaveGuarantorGeneral"
                :name="usersDoYouHaveGuarantorInput"
                :options="usersDoYouHaveGuarantorOptions"
                :value="usersDoYouHaveGuarantorValue"
                v-validate="`make_choice`"
                @input="triggerGuarantorForm"
              ></theme-radios>

              <theme-input
                v-if="guarantor_form_state == 1"
                :label="usersGuarantorIncome"
                :placeholder="usersGuarantorIncomePlaceholder"
                :name="usersGuarantorIncomeInput"
                v-model="users_guarantor_income_value"
                v-validate="`numeric`"
              ></theme-input>
            </div>
          </div>

          <!-- studying -->
          <div v-if="income_form_studying">
            <div class="grid grid-cols-2 gap-5 mb-6 md:gap-8 md:mb-8" v-if="!income_form_work_business">
              <theme-radios
                v-if="!income_form_work_business"
                :label="usersDoYouHaveGuarantorGeneral"
                :name="usersDoYouHaveGuarantorInput"
                :options="usersDoYouHaveGuarantorOptions"
                :value="usersDoYouHaveGuarantorValue"
                v-validate="`make_choice`"
                @input="triggerGuarantorForm"
              ></theme-radios>

              <theme-input
                v-if="guarantor_form_state == 1"
                :label="usersGuarantorIncome"
                :placeholder="usersGuarantorIncomePlaceholder"
                :name="usersGuarantorIncomeInput"
                v-model="users_guarantor_income_value"
                v-validate="`required|numeric`"
              ></theme-input>
            </div>

            <theme-input
              class="mb-8"
              :label="usersStudy"
              :name="usersStudyInput"
              v-model="users_study_value"
              v-validate="`required`"
              :isRequired="true"
              :placeholder="usersStudyPlaceholder"
            ></theme-input>
            
          </div>

          <theme-textarea
            class=""
            v-if="income_form_any"
            :label="usersExtraInformationIncome"
            :name="usersExtraInformationIncomeInput"
            v-model="users_income_note"
            :placeholder="usersExtraInformationIncomePlaceholder"
          ></theme-textarea>
        </div>

        <div class="theme-details-form__living-situation details-section">
          <h2 class="section-title-xs">{{ usersLandlordLabel }}</h2>
          <theme-radios
            :name="usersLandlordRadio"
            :options="usersLandlordOptions"
            v-model="landlord_type_state"
            @input="triggerLandlordType"
            :columnDirection="true"
          ></theme-radios>

          <div v-if="landlord_type_state == 'renting_property'" class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
            <theme-input
              :label="usersLandlordNameLabel"
              :name="usersLandlordName"
              v-model="users_landlord_name_value"
              :placeholder="usersLandlordNamePlaceholder"
            ></theme-input>

            <theme-input
              :label="usersLandlordEmailLabel"
              :name="usersLandlordEmail"
              v-model="users_landlord_email_value"
              v-validate="`email`"
              :placeholder="usersLandlordEmailPlaceholder"
            ></theme-input>

            <theme-input
              :label="usersLandlordPhoneLabel"
              :name="usersLandlordPhone"
              v-model="users_landlord_phone_number_value"
              v-validate="`phone_number`"
              :placeholder="usersLandlordPhonePlaceholder"
            ></theme-input>

          </div>
        </div>

        <div class="theme-details-form__address details-section">
          <h2 class="section-title-xs">{{ subscriptionsYourAddress }}</h2>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
            <theme-select
              :name="addressCountryCodeName"
              :label="locales && locales.users && locales.users.country_code"
              :value="subscriptions_country_code_value"
              :options="countryCodeOptions"
              @input="onAddressCountryChanged"
            ></theme-select>
          </div>

          <!--
            Google Places Autocomplete for a country Google's Address Validation covers (never
            NL - the existing checkPcHn/checkPostcodeAddress blur lookups below stay the ONLY
            Dutch address flow, completely unchanged). Fills the same post_code/city/street/
            house_number fields below rather than replacing them, exactly like add-house.blade.php's
            own onInternationalAddressResolved.
          -->
          <google-address-typeahead
            v-if="isAddressGoogleSupported"
            :country-code="subscriptions_country_code_value"
            :suggest-url="internationalAddressSuggestUrl"
            :validate-url="internationalAddressValidateUrl"
            :locale="locale"
            theme="public"
            @resolved="onPersonalAddressResolved"
          ></google-address-typeahead>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
            <theme-input
              :name="subscriptionsPostCode"
              :label="locales && locales.users && locales.users.post_code"
              :placeholder="subscriptionsPostCodePlaceholder"
              class="js-postcode"
              v-model="subscriptions_post_code_value"
              @blur="checkPcHn($event)"
            ></theme-input>

            <theme-input
              :name="subscriptionsCity"
              :label="locales && locales.users && locales.users.city"
              :placeholder="subscriptionsCityPlaceholder"
              v-model="subscriptions_city_value"
            ></theme-input>
          </div>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
            <theme-input
              class="col-span-1 stretch-height"
              :name="subscriptionsStreet"
              :label="locales && locales.users && locales.users.street"
              :placeholder="subscriptionsStreetPlaceholder"
              v-model="subscriptions_street_value"
            ></theme-input>

            <div class="col-span-1 md:col-span-2 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
              <theme-input
                :name="subscriptionsHouseNumber"
                :label="locales && locales.users && locales.users.house_number"
                :placeholder="subscriptionsHouseNumberPlaceholder"
                class="js-house-number stretch-height"
                v-model="subscriptions_house_number_value"
                @blur="checkPostcodeAddress($event)"
              ></theme-input>

              <theme-input
                class="stretch-height"
                :name="subscriptionsHouseNumberAppendix"
                :label="subscriptionsHouseNumberAppendixLabel"
                :placeholder="subscriptionsHouseNumberAppendixPlaceholder"
                v-model="subscriptionsHouseNumberAppendixValue"
              ></theme-input>
            </div>
          </div>

          <!--
            Never real form fields the candidate edits directly - address_source/validation_status/
            provider_reference/resolution_id only ever come from a Google resolve above, so
            AccountController::update() can server-side re-verify a claimed "verified"/
            "correction_requires_confirmation" status the same way the property save controllers
            already do (App\Services\Address\AddressResolutionStore), rather than trusting it as
            submitted.
          -->
          <input type="hidden" :name="addressAdministrativeAreaName" v-model="subscriptions_administrative_area_value">
          <input type="hidden" :name="addressLine2Name" v-model="subscriptions_address_line_2_value">
          <input type="hidden" :name="addressSourceName" v-model="subscriptions_address_source_value">
          <input type="hidden" :name="addressValidationStatusName" v-model="subscriptions_validation_status_value">
          <input type="hidden" :name="addressProviderReferenceName" v-model="subscriptions_provider_reference_value">
          <input type="hidden" :name="addressResolutionIdName" v-model="subscriptions_resolution_id_value">
        </div>

        <div class="theme-details-form__family-status details-section">
          <h2 class="section-title-xs">{{locales && locales.subscription && locales.subscription.family_status}}</h2>

          <div class="grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">
            <div class="col-span-1">
              <theme-select
                :label="subscriptionsCivilState"
                :value="subscriptionsCivilStateValue"
                :name="subscriptionsCivilStateInput"
                :options="subscriptionsCivilStateOptions"
                v-validate="`required`"
              ></theme-select>
            </div>
            <div class="col-span-1 md:col-span-2 grid grid-cols-1 gap-5 md:grid-cols-3 md:gap-8">

              <theme-select
                :label="subscriptionsChildren"
                :value="subscriptions_children_value"
                name="have_children"
                :options="subscriptionsChildrenOptions"
                show-zero-option="0"
                @input="triggerChildrenForm"
              ></theme-select>

              <theme-select
                :label="subscriptionsPets"
                :value="subscriptions_pets_value"
                name="have_pets"
                :options="subscriptionsPetsOptions"
                show-zero-option="0"
                @input="triggerPetsForm"
              ></theme-select>

              <theme-input
                class="md:col-start-1"
                v-if="subscriptions_children_value == 1"
                :label="subscriptionsNumberOfChildren"
                :placeholder="subscriptionsNumberOfChildrenPlaceholder"
                name="number_of_children"
                v-model="subscriptions_number_of_children_value"
                type="number"
                v-validate="`required`"
                :isRequired="true"
              ></theme-input>

              <theme-input
                class="md:col-start-2"
                v-if="subscriptions_pets_value == 1"
                :label="subscriptionsPetsNote"
                :placeholder="subscriptionsPetsNotePlaceholder"
                name="pets_note"
                v-model="subscriptions_pets_note_value"
                v-validate="`required`"
                :isRequired="true"
              ></theme-input>
            </div>
          </div>
        </div>

      </div>

      <theme-toggler
        :value="subscriptionsSmokingValue"
        :label="subscriptionsSmoking"
        :name="'smoking'"
        class="mb-8"
      ></theme-toggler>
      </div>

      <!-- partners forms: one panel per further person (never on staffhousing, see personCount) -->
      <div
        v-for="adult in personCount - 1"
        :key="adult"
        v-show="active_person === adult + 1"
        class="theme-details-form__person-panel"
        role="tabpanel"
        :id="personPanelId(adult + 1)"
        :aria-labelledby="personTabId(adult + 1)"
        :data-person-panel="adult + 1"
      >
        <theme-details-inner-form
          @label-parts="onExtraPersonLabelParts(adult - 1, $event)"
          :i="adult - 1"
          :extra-tenants="extraTenants"
          :subscriptions="innerFormSubscriptions"
          :users-title-options="innerFormUsersTitleOptions"
          :users-first-name="subscriptionsFirstName"
          :users-middle-name="subscriptionsMiddleName"
          :users-last-name="subscriptionsLastName"
          :users-date-of-birth="subscriptionsDateOfBirth"
          :users-phone-number="subscriptionsPhoneNumber"
          :users-email="subscriptionsEmail"
          :users-invoice-email="subscriptionsInvoiceEmail"
          :users-bank-account="subscriptionsBankAccount"
          :users-bic="subscriptionsBic"
          :users-civil-state="subscriptionsCivilState"
          :users-civil-state-options="subscriptionsCivilStateOptions"
          :users-type-of-income-options="subscriptionsTypeOfIncomeOptions"
          :users-type-of-income-slugs="subscriptionsTypeOfIncomeSlugs"
          :users-contract-type="usersContractTypeLabel"
          :users-contract-type-options="usersContractTypeOptions"
          :users-contract-type-attributes="usersContractTypeAttributes"
          :users-contract-duration="usersContractDuration"
          :users-contract-end-date="usersContractEndDate"
          :users-occupation="usersOccupation"
          :users-monthly-income="usersMonthlyIncome"
          :users-monthly-income-note="usersMonthlyIncomeNote"
          :users-do-you-have-guarantor-general="usersDoYouHaveGuarantorGeneral"
          :users-do-you-have-guarantor-options="usersDoYouHaveGuarantorOptions"
          :users-guarantor-income="usersGuarantorIncome"
          :users-study="usersStudy"
          :users-extra-information-income="usersExtraInformationIncome"
          :income-form-all-states="incomeFormAllStates"
          :income-title="subscriptionsIncome"
          :users-landlord-options="usersLandlordOptions"
          :is-user-already-exists="isUserAlreadyExists"
          :is-user-banned="isUserBanned"
          :income-phone-number-already-exists="phoneNumberAlreadyExists"
          :languages="languages"
          :details-title="subscriptionsDetails"
          :address-title="subscriptionsAddress"
          :family-status-title="subscriptionsFamilyStatus"
          :users-house-number-appendix="subscriptionsHouseNumberAppendix" 
          :users-house-number-appendix-label="subscriptionsHouseNumberAppendixLabel" 
          :users-house-number-appendix-placeholder="subscriptionsHouseNumberPlaceholder" 
          :users-house-number-appendix-value="subscriptionsHouseNumberAppendixValue"
          :users-house-number-appendix-extra="subscriptionsHouseNumberAppendixExtra" 
          :users-house-number-appendix-extra-label="subscriptionsHouseNumberAppendixExtraLabel" 
          :users-house-number-appendix-extra-placeholder="subscriptionsHouseNumberAppendixExtraPlaceholder" 
          :users-house-number-appendix-extra-value="subscriptionsHouseNumberAppendixExtraValue"
          :users-landlord-label="usersLandlordLabel"
          :users-first-name-placeholder="subscriptionsFirstNamePlaceholder"
          :users-middle-name-placeholder="subscriptionsMiddleNamePlaceholder"
          :users-last-name-placeholder="subscriptionsLastNamePlaceholder"
          :users-phone-number-placeholder="subscriptionsPhoneNumberPlaceholder"
          :users-email-placeholder="subscriptionsEmailPlaceholder"
          :users-birth-placeholder="subscriptionsDateOfBirthPlaceholder"
          :users-invoice-email-placeholder="subscriptionsInvoiceEmailPlaceholder"
          :users-bank-account-placeholder="subscriptionsBankAccountPlaceholder"
          :users-bic-placeholder="subscriptionsBicPlaceholder"
          :users-post-code-placeholder="subscriptionsPostCodePlaceholder"
          :users-city-placeholder="subscriptionsCityPlaceholder"
          :users-street-placeholder="subscriptionsStreetPlaceholder"
          :users-contract-duration-placeholder="usersContractDurationPlaceholder"
          :users-contract-end-date-placeholder="usersContractEndDatePlaceholder"
          :users-occupation-placeholder="usersOccupationPlaceholder"
          :users-monthly-income-placeholder="usersMonthlyIncomePlaceholder"
          :users-guarantor-income-placeholder="usersGuarantorIncomePlaceholder"
          :users-study-placeholder="usersStudyPlaceholder"
          :users-extra-information-income-placeholder="usersExtraInformationIncomePlaceholder"
          :users-landlord-name-placeholder="usersLandlordNamePlaceholder"
          :users-landlord-phone-placeholder="usersLandlordPhonePlaceholder"
          :users-landlord-email-placeholder="usersLandlordEmailPlaceholder"
          :users-house-number-placeholder="subscriptionsHouseNumberPlaceholder"
        ></theme-details-inner-form>
      </div>

      <div v-if="website !== 'staffhousing'">
        <input name="is_newsletter_subscriber" type="hidden" />
        <theme-checkboxes
          :label="subscribeToNewsletter"
          class="theme-details-form__reverted-checkbox"
          name="is_newsletter_subscriber"
          :options="{ '1': '' }"
          :value="isNewsletterSubscriberChecked"
          :checked="isNewsletterSubscriberChecked"
        ></theme-checkboxes>
      </div>

      <button v-if="!frozen" class="theme-btn theme-btn--theme w-full md:w-auto" type="submit" @click.prevent="submitForm">
          {{ commonSend }}
      </button>
      <p v-else class="theme-details-form__frozen-message" id="profile-frozen-message">{{ frozenMessage }}</p>

    </div>
  </div>
</template>
<script>
import { FunctionalCalendar } from 'vue-functional-calendar';
import sweetalert from "sweetalert";
import { mapGetters, mapActions } from "vuex";

export default {
  $_veeValidate: {
    validator: "new",
  },
  components: { FunctionalCalendar },
  data() {
    return {
      company_value: this.companyValue,
      subscriptions_first_name_value: this.subscriptionsFirstNameValue,
      subscriptions_middle_name_value: this.subscriptionsMiddleNameValue,
      subscriptions_last_name_value: this.subscriptionsLastNameValue,
      subscriptions_phone_number_value: this.subscriptionsPhoneNumberValue,
      subscriptions_email_value: this.subscriptionsEmailValue,
      subscriptions_invoice_email_value: this.subscriptionsInvoiceEmailValue,
      subscriptions_bank_account_value: this.subscriptionsBankAccountValue,
      subscriptions_bic_value: this.subscriptionsBicValue,
      subscriptions_pets_note_value: this.subscriptionsPetsNoteValue,
      company_legal_form_value: this.companyLegalFormValue,
      company_chamber_of_commerce_number_value: this.companyChamberOfCommerceNumberValue,
      company_vat_number_value: this.companyVatNumberValue,
      company_country_value: this.companyCountryValue,
      company_phone_number_value: this.companyPhoneNumberValue,
      company_email_value: this.companyEmailValue,
      company_bank_account_value: this.companyBankAccountValue,
      company_bic_value: this.companyBicValue,
      company_invoice_email_value: this.companyInvoiceEmailValue,
      users_monthly_income_value: this.usersMonthlyIncomeValue,
      users_occupation_value: this.usersOccupationValue,
      users_guarantor_income_value: this.usersGuarantorIncomeValue,
      users_study_value: this.usersStudyValue,
      users_income_note: this.usersIncomeNote,
      users_landlord_name_value: this.usersLandlordNameValue,
      users_landlord_email_value: this.usersLandlordEmailValue,
      users_landlord_phone_number_value: this.usersLandlordPhoneValue,
      subscriptions_country_code_value: this.subscriptionsCountryCodeValue || 'NL',
      subscriptions_administrative_area_value: '',
      subscriptions_address_line_2_value: '',
      subscriptions_address_source_value: '',
      subscriptions_validation_status_value: '',
      subscriptions_provider_reference_value: '',
      subscriptions_resolution_id_value: '',
      company_country_code_value: this.companyCountryCodeValue || 'NL',
      company_administrative_area_value: '',
      company_address_line_2_value: '',
      company_address_source_value: '',
      company_validation_status_value: '',
      company_provider_reference_value: '',
      company_resolution_id_value: '',
      birthDate: { date: this.subscriptionsDateOfBirthValue },
      contractEndDate: { date: this.usersContractEndDateValue },
      income_form_any: false,
      income_form_studying: false,
      income_form_work: false,
      income_form_work_business: false,
      business_form_state: this.businessFormState,
      income_form_state: this.incomeFormState,
      income_form_all_states: this.incomeFormAllStates,
      users_do_you_have_guarantor_value: this.usersDoYouHaveGuarantorValue,
      landlord_type_state: this.landlordTypeState,
      is_phone_number_unique: 0,
      is_user_already_exists: 0,
      subscriptions_post_code_value: this.subscriptionsPostCodeValue,
      subscriptions_city_value: this.subscriptionsCityValue,
      subscriptions_street_value: this.subscriptionsStreetValue,
      subscriptions_house_number_value: this.subscriptionsHouseNumberValue,
      number_of_adults_value: this.subscriptionNumberOfAdultsValue,
      subscriptions_children_value: this.subscriptionsChildrenValue,
      subscriptions_number_of_children_value: this.subscriptionsNumberOfChildrenValue,
      subscriptions_pets_value: this.subscriptionsPetsValue,
      company_post_code_value: this.companyPostCodeValue,
      company_house_number_value: this.companyHouseNumberValue,
      company_street_value: this.companyStreetValue,
      company_city_value: this.companyCityValue,
      showDuration: false,
      guarantor_form_state: this.guarantorFormState,
      hiddenContractEndDate: moment(this.usersContractEndDateValue,'DD/MM/YYYY').format('YYYY-MM-DD'),
      // Person selector: the shown person (1 = the contact person), Person 1's chosen title and
      // the names the further persons' forms report, so the tab labels follow what is typed.
      active_person: 1,
      primary_title_id: this.subscriptionsSexValue,
      extra_person_label_parts: {},

      calendarConfigs: {
        dateFormat: 'dd-mm-yyyy',
        isModal: true,
        isDatePicker: true,
        isAutoCloseable: true,
        changeYearFunction: true,
      },
    };
  },
  computed: {
    ...mapGetters(["locales"]),
    // The persons with a panel: the household size, but only the contact person on staffhousing,
    // which never had the further persons' forms.
    personCount: function () {
      if (this.website === "staffhousing") {
        return 1;
      }
      var count = parseInt(this.number_of_adults_value, 10);
      return count >= 1 ? count : 1;
    },
    persons: function () {
      var persons = [];
      for (var number = 1; number <= this.personCount; number++) {
        persons.push({ number: number, label: this.personLabel(number) });
      }
      return persons;
    },
    website: function () {
      if (this.websiteId != 2) {
        return "athome";
      } else {
        return "staffhousing";
      }
    },
    birthDateComputed: function() {
      return this.birthDate.date ? this.birthDate.date : '';
    },
    contractEndDateComputed: function() {
      return this.contractEndDate.date ? this.contractEndDate.date : '';
    },
    firstLetterFirstnameComputed: function () {
      return this.subscriptionsFirstNameValue[0];
    },
    firstLetterLastOrMiddlenameComputed: function () {
      return this.subscriptionsLastNameValue[0] || this.subscriptionsMiddleNameValue[0];
    },
    // Personal-address and company-address Dutch-only guards - never NL by omission, only by an
    // explicit country_code match, mirroring add-house.blade.php's isNLProperty/
    // isGoogleSupportedCountry.
    isAddressNL: function () {
      return (this.subscriptions_country_code_value || 'NL').toUpperCase() === 'NL';
    },
    isAddressGoogleSupported: function () {
      return !this.isAddressNL && this.googleSupportedCountries.indexOf((this.subscriptions_country_code_value || '').toUpperCase()) !== -1;
    },
    isCompanyAddressNL: function () {
      return (this.company_country_code_value || 'NL').toUpperCase() === 'NL';
    },
    isCompanyAddressGoogleSupported: function () {
      return !this.isCompanyAddressNL && this.googleSupportedCountries.indexOf((this.company_country_code_value || '').toUpperCase()) !== -1;
    },
    // subscriptionsPostCode/companyPostCodeInput are already the exact "person[id][address]
    // [post_code]"/"person[id][company][post_code]" input names this form submits - deriving the
    // new hidden fields' names from the same string keeps them attached to the same person/company
    // entry without a dozen new individually-named blade props.
    addressFieldPrefix: function () {
      return (this.subscriptionsPostCode || '').replace('[post_code]', '');
    },
    companyFieldPrefix: function () {
      return (this.companyPostCodeInput || '').replace('[post_code]', '');
    },
    addressCountryCodeName: function () {
      return this.addressFieldPrefix + '[country_code]';
    },
    addressAdministrativeAreaName: function () {
      return this.addressFieldPrefix + '[administrative_area]';
    },
    addressLine2Name: function () {
      return this.addressFieldPrefix + '[address_line_2]';
    },
    addressSourceName: function () {
      return this.addressFieldPrefix + '[address_source]';
    },
    addressValidationStatusName: function () {
      return this.addressFieldPrefix + '[validation_status]';
    },
    addressProviderReferenceName: function () {
      return this.addressFieldPrefix + '[provider_reference]';
    },
    addressResolutionIdName: function () {
      return this.addressFieldPrefix + '[resolution_id]';
    },
    companyCountryCodeName: function () {
      return this.companyFieldPrefix + '[country_code]';
    },
    companyAdministrativeAreaName: function () {
      return this.companyFieldPrefix + '[administrative_area]';
    },
    companyAddressLine2Name: function () {
      return this.companyFieldPrefix + '[address_line_2]';
    },
    companyAddressSourceName: function () {
      return this.companyFieldPrefix + '[address_source]';
    },
    companyValidationStatusName: function () {
      return this.companyFieldPrefix + '[validation_status]';
    },
    companyProviderReferenceName: function () {
      return this.companyFieldPrefix + '[provider_reference]';
    },
    companyResolutionIdName: function () {
      return this.companyFieldPrefix + '[resolution_id]';
    },
  },
  watch: {
    // Fewer persons: a removed active person hands over to the nearest one that is left.
    personCount: function (count) {
      if (this.active_person > count) {
        this.active_person = count;
      }
    },
  },
  props: {
    websiteId: {
      default: "",
      type: String,
    },
    // Country-aware international-address support shared by the personal-address and
    // company-address blocks below (see InternationalAddressLookupController) - countryCodeOptions
    // and the suggest/validate URLs are provider-agnostic and reused by both blocks unchanged.
    locale: {
      default: "nl",
      type: String,
    },
    countryCodeOptions: {
      default: function () { return {}; },
      type: Object,
    },
    googleSupportedCountries: {
      default: function () { return []; },
      type: Array,
    },
    internationalAddressSuggestUrl: {
      default: "",
      type: String,
    },
    internationalAddressValidateUrl: {
      default: "",
      type: String,
    },
    subscriptionsCountryCodeValue: {
      default: "NL",
      type: String,
    },
    companyCountryCodeValue: {
      default: "NL",
      type: String,
    },
    srcInfoText: {
      default: '',
      type: String,
    },
    isSearchProfileCategory: {
      default:  0,
      type: Number,
    },
    minPrice: {
      default: 0,
      type: Number,
    },
    requestSrcButton: {
      default: '',
      type: String,
    },
    accountInformationText: {
      default: '',
      type: String,
    },
    languages: {
      default: () => {},
      type: Object,
    },
    languageLabel: {
      default: '',
      type: String,
    },
    defaultLanguage: {
      default: '',
      type: String,
    },
    businessLabel: {
      default: "",
      type: String,
    },
    businessRadioOptions: {
      default: () => {},
      type: Object,
    },
    businessFormState: {
      default: "",
      type: String,
    },
    subscriptionsIncome: {
      default: "",
      type: String,
    },
    subscriptionsTypeOfIncomeValue: {
      default: () => [],
      type: Array,
    },
    subscriptionsTypeOfIncomeInput: {
      default: "",
      type: String,
    },
    subscriptionsTypeOfIncomeOptions: {
      default: () => {},
      type: Object,
    },
    usersContractTypeLabel: {
      default: "",
      type: String,
    },
    usersContractTypeInput: {
      default: "",
      type: String,
    },
    usersContractTypeValue: {
      default: "",
      type: String,
    },
    usersContractTypeOptions: {
      default: () => {},
      type: [Object, Array],
    },
    usersOccupation: {
      default: "",
      type: String,
    },
    usersOccupationInput: {
      default: "",
      type: String,
    },
    usersOccupationValue: {
      default: "",
      type: String,
    },
    usersOccupationPlaceholder: {
      default: "",
      type: String,
    },
    usersMonthlyIncome: {
      default: "",
      type: String,
    },
    usersMonthlyIncomeInput: {
      default: "",
      type: String,
    },
    usersMonthlyIncomeValue: {
      default: "",
      type: String,
    },
    usersMonthlyIncomePlaceholder: {
      default: "",
      type: String,
    },
    usersDoYouHaveGuarantorGeneral: {
      default: "",
      type: String,
    },
    usersDoYouHaveGuarantorInput: {
      default: "",
      type: String,
    },
    usersDoYouHaveGuarantorOptions: {
      default: () => {},
      type: Object,
    },
    usersDoYouHaveGuarantorValue: {
      default: null,
      type: [Object, String],
    },
    usersExtraInformationIncome: {
      default: "",
      type: String,
    },
    usersExtraInformationIncomeInput: {
      default: "",
      type: String,
    },
    usersExtraInformationIncomePlaceholder: {
      default: "",
      type: String,
    },
    usersLandlordLabel: {
      default: "",
      type: String,
    },
    usersLandlordRadio: {
      default: "",
      type: String,
    },
    usersLandlordOptions: {
      default: () => {},
      type: Object,
    },
    landlordTypeState: {
      default: "-1",
      type: String,
    },
    usersLandlordNameLabel: {
      default: "",
      type: String,
    },
    usersLandlordEmailLabel: {
      default: "",
      type: String,
    },
    usersLandlordPhoneLabel: {
      default: "",
      type: String,
    },
    usersLandlordName: {
      default: "",
      type: String,
    },
    usersLandlordEmail: {
      default: "",
      type: String,
    },
    usersLandlordPhone: {
      default: "",
      type: String,
    },
    usersLandlordNameValue: {
      default: "",
      type: String,
    },
    usersLandlordEmailValue: {
      default: "",
      type: String,
    },
    usersLandlordPhoneValue: {
      default: "",
      type: String,
    },
    subscriptionsYourDetails: {
      default: "",
      type: String,
    },
    subscriptionsYourAddress: {
      default: "",
      type: String,
    },
    subscriptionsDetails: {
      default: "",
      type: String,
    },
    subscriptionsFirstName: {
      default: "",
      type: String,
    },
    subscriptionsFirstNameInput: {
      default: "",
      type: String,
    },
    subscriptionsFirstNameValue: {
      default: "",
      type: String,
    },
    subscriptionsFirstNamePlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsMiddleName: {
      default: "",
      type: String,
    },
    subscriptionsMiddleNameInput: {
      default: "",
      type: String,
    },
    subscriptionsMiddleNameValue: {
      default: "",
      type: String,
    },
    subscriptionsMiddleNamePlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsLastName: {
      default: "",
      type: String,
    },
    subscriptionsLastNameInput: {
      default: "",
      type: String,
    },
    subscriptionsLastNameValue: {
      default: "",
      type: String,
    },
    subscriptionsLastNamePlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsPhoneNumber: {
      default: "",
      type: String,
    },
    subscriptionsPhoneNumberInput: {
      default: "",
      type: String,
    },
    subscriptionsPhoneNumberPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsPhoneNumberValue: {
      default: "",
      type: String,
    },
    phoneNumberAlreadyExists: {
      default: "",
      type: String,
    },
    subscriptionsEmail: {
      default: "",
      type: String,
    },
    subscriptionsEmailInput: {
      default: "",
      type: String,
    },
    subscriptionsEmailPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsEmailValue: {
      default: "",
      type: String,
    },
    isUserAlreadyExists: {
      default: "",
      type: String,
    },
    subscriptionsInvoiceEmail: {
      default: "",
      type: String,
    },
    subscriptionsInvoiceEmailInput: {
      default: "",
      type: String,
    },
    subscriptionsInvoiceEmailPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsInvoiceEmailValue: {
      default: "",
      type: String,
    },
    subscriptionsBankAccount: {
      default: "",
      type: String,
    },
    subscriptionsBankAccountInput: {
      default: "",
      type: String,
    },
    subscriptionsBankAccountPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsBankAccountValue: {
      default: "",
      type: String,
    },
    subscriptionsBic: {
      default: "",
      type: String,
    },
    subscriptionsBicInput: {
      default: "",
      type: String,
    },
    subscriptionsBicPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsBicValue: {
      default: "",
      type: String,
    },
    subscriptionsPostCode: {
      default: "",
      type: String,
    },
    subscriptionsPostCodePlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsPostCodeValue: {
      default: "",
      type: String,
    },
    subscriptionsCity: {
      default: "",
      type: String,
    },
    subscriptionsCityPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsCityValue: {
      default: "",
      type: String,
    },
    subscriptionsStreet: {
      default: "",
      type: String,
    },
    subscriptionsStreetPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsStreetValue: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumber: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberValue: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendix: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixLabel: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixValue: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixExtra: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixExtraLabel: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixExtraPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsHouseNumberAppendixExtraValue: {
      default: "",
      type: String,
    },
    subscriptionsCivilState: {
      default: "",
      type: String,
    },
    subscriptionsCivilStateInput: {
      default: "",
      type: String,
    },
    subscriptionsCivilStateOptions: {
      default: () => {},
      type: Object,
    },
    subscriptionsCivilStateValue: {
      default: "",
      type: String,
    },
    subscriptionNumberOfAdults: {
      default: "",
      type: String,
    },
    subscriptionNumberOfAdultsValue: {
      default: "",
      type: String,
    },
    subscriptionsChildren: {
      default: "",
      type: String,
    },
    subscriptionsChildrenValue: {
      default: "",
      type: String,
    },
    subscriptionsChildrenOptions: {
      default: () => {},
      type: Object,
    },
    subscriptionsNumberOfChildren: {
      default: "",
      type: String,
    },
    subscriptionsNumberOfChildrenPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsNumberOfChildrenValue: {
      default: "",
      type: String,
    },
    subscriptionsPets: {
      default: "",
      type: String,
    },
    subscriptionsPetsOptions: {
      default: () => {},
      type: Object,
    },
    subscriptionsPetsValue: {
      default: "",
      type: String,
    },
    subscriptionsPetsNote: {
      default: "",
      type: String,
    },
    subscriptionsPetsNotePlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsPetsNoteValue: {
      default: "",
      type: String,
    },
    subscriptionsSmoking: {
      default: "",
      type: String,
    },
    subscriptionsSmokingValue: {
      default: "",
      type: String,
    },
    subscriptionsSmokingOptions: {
      default: () => {},
      type: Object,
    },
    subscribeToNewsletter: {
      default: "",
      type: String,
    },
    isNewsletterSubscriberChecked: {
      default: 0,
      type: Number,
    },
    commonSend: {
      default: "",
      type: String,
    },
    // A frozen dossier: the server refuses a save, so the form says why instead of offering one.
    frozen: {
      default: false,
      type: Boolean,
    },
    frozenMessage: {
      default: "",
      type: String,
    },
    companyName: {
      default: "",
      type: String,
    },
    companyNameInput: {
      default: "",
      type: String,
    },
    companyNamePlaceholder: {
      default: "",
      type: String,
    },
    companyValue: {
      default: "",
      type: String,
    },
    companyLegalForm: {
      default: "",
      type: String,
    },
    companyLegalFormInput: {
      default: "",
      type: String,
    },
    companyLegalFormPlaceholder: {
      default: "",
      type: String,
    },
    companyLegalFormValue: {
      default: "",
      type: String,
    },
    companyChamberOfCommerceNumber: {
      default: "",
      type: String,
    },
    companyChamberOfCommerceNumberInput: {
      default: "",
      type: String,
    },
    companyChamberOfCommerceNumberPlaceholder: {
      default: "",
      type: String,
    },
    companyChamberOfCommerceNumberValue: {
      default: "",
      type: String,
    },
    companyVatNumber: {
      default: "",
      type: String,
    },
    companyVatNumberInput: {
      default: "",
      type: String,
    },
    companyVatNumberPlaceholder: {
      default: "",
      type: String,
    },
    companyVatNumberValue: {
      default: "",
      type: String,
    },
    companyPostCode: {
      default: "",
      type: String,
    },
    companyPostCodeInput: {
      default: "",
      type: String,
    },
    companyPostCodePlaceholder: {
      default: "",
      type: String,
    },
    companyPostCodeValue: {
      default: "",
      type: String,
    },
    companyHouseNumber: {
      default: "",
      type: String,
    },
    companyHouseNumberInput: {
      default: "",
      type: String,
    },
    companyHouseNumberPlaceholder: {
      default: "",
      type: String,
    },
    companyHouseNumberValue: {
      default: "",
      type: String,
    },
    companyStreet: {
      default: "",
      type: String,
    },
    companyStreetInput: {
      default: "",
      type: String,
    },
    companyStreetPlaceholder: {
      default: "",
      type: String,
    },
    companyStreetValue: {
      default: "",
      type: String,
    },
    companyCity: {
      default: "",
      type: String,
    },
    companyCityInput: {
      default: "",
      type: String,
    },
    companyCityPlaceholder: {
      default: "",
      type: String,
    },
    companyCityValue: {
      default: "",
      type: String,
    },
    companyCountry: {
      default: "",
      type: String,
    },
    companyCountryInput: {
      default: "",
      type: String,
    },
    companyCountryPlaceholder: {
      default: "",
      type: String,
    },
    companyCountryValue: {
      default: "",
      type: String,
    },
    companyPhoneNumber: {
      default: "",
      type: String,
    },
    companyPhoneNumberInput: {
      default: "",
      type: String,
    },
    companyPhoneNumberPlaceholder: {
      default: "",
      type: String,
    },
    companyPhoneNumberValue: {
      default: "",
      type: String,
    },
    companyEmail: {
      default: "",
      type: String,
    },
    companyEmailInput: {
      default: "",
      type: String,
    },
    companyEmailPlaceholder: {
      default: "",
      type: String,
    },
    companyEmailValue: {
      default: "",
      type: String,
    },
    companyInvoiceEmail: {
      default: "",
      type: String,
    },
    companyInvoiceEmailInput: {
      default: "",
      type: String,
    },
    companyInvoiceEmailPlaceholder: {
      default: "",
      type: String,
    },
    companyInvoiceEmailValue: {
      default: "",
      type: String,
    },
    companyBankAccount: {
      default: "",
      type: String,
    },
    companyBankAccountInput: {
      default: "",
      type: String,
    },
    companyBankAccountPlaceholder: {
      default: "",
      type: String,
    },
    companyBankAccountValue: {
      default: "",
      type: String,
    },
    companyBic: {
      default: "",
      type: String,
    },
    companyBicInput: {
      default: "",
      type: String,
    },
    companyBicPlaceholder: {
      default: "",
      type: String,
    },
    companyBicValue: {
      default: "",
      type: String,
    },
    incomeFormState: {
      default: () => [],
      type: Array,
    },
    incomeFormAllStates: {
      default: () => [],
      type: Array,
    },
    usersContractTypeAttributes: {
      default: () => {},
      type: [Object, Array],
    },
    usersContractDuration: {
      default: "",
      type: String,
    },
    usersContractDurationInput: {
      default: "",
      type: String,
    },
    usersContractDurationPlaceholder: {
      default: "",
      type: String,
    },
    usersContractDurationValue: {
      default: "",
      type: String,
    },
    usersContractEndDate: {
      default: "",
      type: String,
    },
    usersContractEndDateInput: {
      default: "",
      type: String,
    },
    usersContractEndDatePlaceholder: {
      default: "",
      type: String,
    },
    usersContractEndDateValue: {
      default: "",
      type: String,
    },
    usersMonthlyIncomeNote: {
      default: "",
      type: String,
    },
    guarantorFormState: {
      default: "",
      type: String,
    },
    usersGuarantorIncome: {
      default: "",
      type: String,
    },
    usersGuarantorIncomeInput: {
      default: "",
      type: String,
    },
    usersGuarantorIncomePlaceholder: {
      default: "",
      type: String,
    },
    usersGuarantorIncomeValue: {
      default: "",
      type: String,
    },
    usersStudy: {
      default: "",
      type: String,
    },
    usersStudyInput: {
      default: "",
      type: String,
    },
    usersStudyPlaceholder: {
      default: "",
      type: String,
    },
    usersStudyValue: {
      default: "",
      type: String,
    },
    subscriptionsTypeOfIncomeSlugs: {
      default: () => {},
      type: Object,
    },
    subscriptionsSexInput: {
      default: "",
      type: String,
    },
    subscriptionsSexOptions: {
      default: () => {},
      type: Object,
    },
    subscriptionsSexValue: {
      default: "",
      type: String,
    },
    subscriptionsDateOfBirth: {
      default: "",
      type: String,
    },
    subscriptionsDateOfBirthInput: {
      default: "",
      type: String,
    },
    subscriptionsDateOfBirthPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsDateOfBirthValue: {
      default: "",
      type: String,
    },
    extraTenants: {
      default: () => {},
      type: [Object, Array],
    },
    innerFormSubscriptions: {
      default: "",
      type: String,
    },
    innerFormUsersTitleOptions: {
      default: () => {},
      type: Object,
    },
    isUserBanned: {
      default: "",
      type: String,
    },
    subscriptionsDetails: {
      default: "",
      type: String,
    },
    subscriptionsAddress: {
      default: "",
      type: String,
    },
    subscriptionsFamilyStatus: {
      default: "",
      type: String,
    },
    usersIncomeNote: {
      default: "",
      type: String,
    },
    usersLandlordNamePlaceholder: {
      default: "",
      type: String,
    },
    usersLandlordPhonePlaceholder: {
      default: "",
      type: String,
    },
    usersLandlordEmailPlaceholder: {
      default: "",
      type: String,
    },
    subscriptionsIAm: {
      default: "",
      type: String,
    },
  },
  methods: {
    personTabId: function (number) {
      return "theme-details-person-tab-" + number;
    },
    personPanelId: function (number) {
      return "theme-details-person-panel-" + number;
    },
    selectPerson: function (number) {
      this.active_person = number;
    },
    // Arrow keys, Home and End move between the person tabs (WAI-ARIA tabs pattern).
    onPersonTabKeydown: function (event, number) {
      var keys = { ArrowRight: number + 1, ArrowDown: number + 1, ArrowLeft: number - 1, ArrowUp: number - 1, Home: 1, End: this.personCount };
      if (!keys.hasOwnProperty(event.key)) {
        return;
      }
      event.preventDefault();
      var target = keys[event.key];
      if (target > this.personCount) {
        target = 1;
      } else if (target < 1) {
        target = this.personCount;
      }
      this.selectPerson(target);
      this.$nextTick(() => {
        var tab = document.getElementById(this.personTabId(target));
        if (tab) {
          tab.focus();
        }
      });
    },
    onExtraPersonLabelParts: function (index, parts) {
      this.$set(this.extra_person_label_parts, index, parts);
    },
    // Same shape as User::fullNameWithTitle, which the upload-documents selector shows: the title
    // and full name, after the company name when there is one. Without a name the translated
    // "Person N" fallback.
    personLabel: function (number) {
      var parts, titleOptions, company = "";
      if (number === 1) {
        parts = {
          title_id: this.primary_title_id,
          first_name: this.subscriptions_first_name_value,
          middle_name: this.subscriptions_middle_name_value,
          last_name: this.subscriptions_last_name_value,
        };
        titleOptions = this.subscriptionsSexOptions;
        company = this.business_form_state === "1" ? (this.company_value || "").trim() : "";
      } else {
        var tenant = this.extraTenants && this.extraTenants[number - 2];
        parts = this.extra_person_label_parts[number - 2] || tenant || {};
        titleOptions = this.innerFormUsersTitleOptions;
      }

      var fullName = [parts.first_name, parts.middle_name, parts.last_name]
        .map(function (part) { return (part || "").toString().trim(); })
        .filter(Boolean)
        .join(" ");
      var title = fullName && titleOptions && parts.title_id ? titleOptions[parts.title_id] || "" : "";
      var name = (title + " " + fullName).trim();

      if (company) {
        return name ? company + " - " + name : company;
      }
      return name || (this.innerFormSubscriptions + " " + number).trim();
    },
    // After a failed validation: when no error is in the shown person's panel, show the panel of
    // the first person with one, so the field can be seen and fixed.
    revealInvalidPerson: function () {
      var personsWithErrors = [];
      this.$validator.errors.items.forEach((error) => {
        var field = this.$validator.fields.find({ id: error.id });
        var panel = field && field.el && field.el.closest ? field.el.closest("[data-person-panel]") : null;
        var number = panel ? parseInt(panel.getAttribute("data-person-panel"), 10) : null;
        if (number && personsWithErrors.indexOf(number) === -1) {
          personsWithErrors.push(number);
        }
      });
      if (personsWithErrors.length && personsWithErrors.indexOf(this.active_person) === -1) {
        this.active_person = personsWithErrors[0];
      }
    },
    triggerBusinessForm: function (value) {
      this.business_form_state = value;
    },
    triggerLandlordType: function (value) {
      this.landlord_type_state = value;
    },
    triggerChildrenForm: function (value) {
      this.subscriptions_children_value = value;
    },
    triggerPetsForm: function (value) {
      this.subscriptions_pets_value = value;
    },
    triggerIncomeAdditionalForms: function (value) {
      if (value) this.income_form_state = value;

      this.income_form_any = false;
      this.income_form_studying = false;
      this.income_form_work_business = false;
      this.income_form_work = false;

      for (var i = 0; i < this.income_form_state.length; i++) {
        var state = this.subscriptionsTypeOfIncomeSlugs[this.income_form_state[i]];

        if (this.income_form_all_states.indexOf(state) > -1) {
          this.income_form_any = true;
          if (state == "own_business" || state == "working") {
            this.income_form_work_business = true;
          }
          if (state == "working") {
            this.income_form_work = true;
          }
          if (state == "studying") {
            this.income_form_studying = true;
          }
        }
      }
    },
    triggerContractDuration: function (value) {
      this.showDuration = false;
      if (value) {
        var option = this.usersContractTypeAttributes[value];
        if (option == "1") {
          this.showDuration = true;
        }
      } else {
        var option = this.usersContractTypeAttributes[
          this.usersContractTypeValue
        ];
        if (option == "1") {
          this.showDuration = true;
        }
      }
    },
    triggerGuarantorForm: function (value) {
      if (value !== true) {
        this.guarantor_form_state = value;
      } else {
        this.guarantor_form_state = -1;
      }
    },
    changeBirthDate: function (value) {
      this.birthDate = { date: this.formatCalendarDate(value) };
    },
    changeContractEndDate: function (value) {
      this.contractEndDate = { date: moment(value.date, 'DD/MM/YYYY').format('DD-MM-YYYY') };
      this.hiddenContractEndDate = moment(value.date,'DD/MM/YYYY').format('YYYY-MM-DD');
    },

    submitForm: function (e) {
      e.preventDefault();
      this.$validator.validateAll().then((res) => {
        if (res) {
          let form = $("#form");
          let data = $("form").serialize();
          $.ajax({
            url: form.attr('action'),
            method: 'post',
            data: data,
            success: function (result) {
              if (result && result.status && result.status == "error") {
                var message = result.message;
                sweetalert(window.app.locales.common.error, message, "error");
              } else {
                sweetalert(
                  window.app.locales.common.success,
                  window.app.locales.common.success_update_details,
                  "success"
                ).then((res) => {
                  window.location.replace(result.url);
                });
              }
            },
            error: function (error) {
              if (error.status == 403 || error.status == 401) {
                sweetalert(
                  window.app.locales.common.error,
                  window.app.locales.common.login_required,
                  "error"
                ).then(() => {
                  window.location.reload();
                });
              } else {
                sweetalert(
                  window.app.locales.common.error,
                  window.app.locales.common.error,
                  "error"
                );
              }
            },
          });
				} else {
          this.revealInvalidPerson();

          let i,
            errorsMsg = "",
            errorsList = document.createElement("ol"),
            errorsListBox = document.createElement("div");

          errorsList.classList.add("custom-errors-list");

          for (i = 0; i < this.$validator.errors.items.length; i++) {
            errorsMsg += "<li>" + this.$validator.errors.items[i].msg + "</li>";
          }

          errorsList.innerHTML = errorsMsg;
          errorsListBox.innerHTML =
            '<strong class="custom-errors-list__subtitle">The form contain some errors:</strong>';
          errorsListBox.appendChild(errorsList);

          sweetalert({
            title: "Opps!",
            content: errorsListBox,
            icon: "error",
          });
        }
      });
    },
    checkPhone: function (event) {
      var phone = event.target.value;
      if (phone == "") return (this.is_phone_number_unique = 0);
      var vm = this;
      var elements = document.getElementsByClassName("js-unique-phone");
      var phones = [];
      Array.prototype.forEach.call(elements, function (el) {
        // Do stuff here
        let val = el.querySelector("input").value;
        if (el.querySelector("input") != event.target && val != "") {
          phones.push(val);
        }
      });
      if (phones.length && phones.indexOf(phone) != -1) {
        vm.is_phone_number_unique = 1;
      } else {
        vm.is_phone_number_unique = 0;
      }
    },
    checkMail: function (event) {
      var email = event.target.value;
      if (email == "") return (this.is_user_already_exists = 0);
      var vm = this;

      $.ajax({
        url: `/api/users/check-mail`,
        method: "post",
        data: {
          email: email,
          id: vm.userId,
        },
        success: function (result) {
          vm.is_user_already_exists = +result;
          if (result == 0) {
            var elements = document.getElementsByClassName("js-unique-mail");
            var mails = [];
            Array.prototype.forEach.call(elements, function (el) {
              // Do stuff here
              let val = el.querySelector("input").value;
              if (el.querySelector("input") != event.target && val != "") {
                mails.push(val);
              }
            });
            if (mails.length && mails.indexOf(email) != -1) {
              vm.is_user_already_exists = 1;
            } else {
              vm.is_user_already_exists = 0;
            }
          }
        },
        error: function () {},
      });
    },
    // A country change invalidates whatever address state was mid-flight for the PREVIOUS
    // country - never carry a resolved/pending Dutch or foreign address over to a newly-selected,
    // different country. Mirrors add-house.blade.php's onCountryChanged.
    onAddressCountryChanged(code) {
      this.subscriptions_country_code_value = code;
      this.subscriptions_post_code_value = '';
      this.subscriptions_city_value = '';
      this.subscriptions_street_value = '';
      this.subscriptions_house_number_value = '';
      this.subscriptions_administrative_area_value = '';
      this.subscriptions_address_line_2_value = '';
      this.subscriptions_address_source_value = '';
      this.subscriptions_validation_status_value = '';
      this.subscriptions_provider_reference_value = '';
      this.subscriptions_resolution_id_value = '';
    },
    // Fills from a Google Address Validation result (App\Services\Address\DTO\NormalizedAddress
    // shape) - the international-address counterpart of the NL-only checkPcHn/checkPostcodeAddress
    // below. Never runs those Dutch-registry-only lookups: this only ever fires for a non-NL,
    // Google-supported country.
    onPersonalAddressResolved(address) {
      this.subscriptions_post_code_value = address.postal_code || '';
      this.subscriptions_city_value = address.locality || '';
      this.subscriptions_street_value = address.street || '';
      this.subscriptions_house_number_value = address.house_number || '';
      this.subscriptions_administrative_area_value = address.administrative_area || '';
      this.subscriptions_address_line_2_value = address.address_line_2 || '';
      this.subscriptions_address_source_value = address.provider || '';
      this.subscriptions_validation_status_value = address.validation_status || '';
      this.subscriptions_provider_reference_value = address.provider_reference || '';
      this.subscriptions_resolution_id_value = address.resolution_id || '';
    },
    onCompanyCountryChanged(code) {
      this.company_country_code_value = code;
      this.company_post_code_value = '';
      this.company_city_value = '';
      this.company_street_value = '';
      this.company_house_number_value = '';
      this.company_administrative_area_value = '';
      this.company_address_line_2_value = '';
      this.company_address_source_value = '';
      this.company_validation_status_value = '';
      this.company_provider_reference_value = '';
      this.company_resolution_id_value = '';
    },
    onCompanyAddressResolved(address) {
      this.company_post_code_value = address.postal_code || '';
      this.company_city_value = address.locality || '';
      this.company_street_value = address.street || '';
      this.company_house_number_value = address.house_number || '';
      this.company_administrative_area_value = address.administrative_area || '';
      this.company_address_line_2_value = address.address_line_2 || '';
      this.company_address_source_value = address.provider || '';
      this.company_validation_status_value = address.validation_status || '';
      this.company_provider_reference_value = address.provider_reference || '';
      this.company_resolution_id_value = address.resolution_id || '';
    },
    checkPcHn(event) {
      // Dutch-only guard: never call the PDOK-backed /get-street-location lookup for a country
      // other than NL - the Google branch above fills these same fields itself.
      if (!this.isAddressNL) {
        return;
      }
      this.validated = false;
      this.changed = true;
      var that = this;
      setTimeout(function () {
        var postcode = $("[name='" + that.subscriptionsPostCode + "']")
          .first()
          .val();
        var house_number = $("[name='" + that.subscriptionsHouseNumber + "']")
          .first()
          .val();
        if (postcode && postcode != "") {
          $.post(
            "/get-street-location",
            {
              postcode: postcode,
              house_number: house_number,
            },
            function (data) {
              if (data.error == "false") {
                $("[name='" + that.subscriptionsCity + "']")
                  .first()
                  .val(data.location);
                that.subscriptions_city_value = data.location;
                $("[name='" + that.subscriptionsStreet + "']")
                  .first()
                  .val(data.street);
                that.subscriptions_street_value = data.street;
              }
            },
            "json"
          );
        }
      }, 100);
    },
    checkPcHnCompany(event) {
      if (!this.isCompanyAddressNL) {
        return;
      }
      this.validated = false;
      this.changed = true;
      var that = this;
      setTimeout(function () {
        var postcode = that.company_post_code_value;
        var house_number =that.company_house_number_value;
        if (postcode && postcode != "") {
          $.post(
            "/get-street-location",
            {
              postcode: postcode,
              house_number: house_number,
            },
            function (data) {
              if (data.error == "false") {
                that.company_street_value = data.street;
                that.company_city_value = data.location;
              }
            },
            "json"
          );
        }
      }, 100);
    },
    checkPostcodeAddress(event) {
      if (!this.isAddressNL) {
        return;
      }
      this.validated = false;
      this.changed = true;
      var that = this;
      setTimeout(function () {
        var street = $("[name='" + that.subscriptionsStreet + "']")
          .first()
          .val();
        var house_number = $("[name='" + that.subscriptionsHouseNumber + "']")
          .first()
          .val();
        var location = $("[name='" + that.subscriptionsCity + "']")
          .first()
          .val();
        if (street != "" && house_number != "") {
          $.post(
            `/get-postcode-by-address`,
            {
              street: street,
              house_number: house_number,
              location: location,
            },
            function (data) {
              if (data.error == "false") {
                $("[name='" + that.subscriptionsPostCode + "']")
                  .first()
                  .val(data.postcode);
                  that.subscriptions_post_code_value = data.postcode;
              }
            },
            "json"
          );
        }
      }, 100);
    },
    checkPostcodeAddressCompany(event) {
      if (!this.isCompanyAddressNL) {
        return;
      }
      this.validated = false;
      this.changed = true;
      var that = this;
      setTimeout(function () {
        var street = that.company_street_value;
        var house_number = that.company_house_number_value;
        var location = that.company_city_value;
        if (street != "" && house_number != "") {
          $.post(
            `/get-postcode-by-address`,
            {
              street: street,
              house_number: house_number,
              location: location,
            },
            function (data) {
              if (data.error == "false") {
                  that.company_post_code_value = data.postcode;
              }
            },
            "json"
          );
        }
      }, 100);
    },
    formatCalendarDate(value) {
      const rawDate = value && value.date ? value.date : '';

      if (!rawDate) {
        return '';
      }

      const date = moment(rawDate, [
        'D-M-YYYY',
        'DD-MM-YYYY',
        'D/M/YYYY',
        'DD/MM/YYYY',
      ], true);

      return date.isValid() ? date.format('DD-MM-YYYY') : rawDate;
    },
  },
    beforeMount() {
      this.triggerIncomeAdditionalForms();
      this.triggerContractDuration();
    },

};
</script>